import type { FC } from 'react'

import { cn } from '@/lib/utils'
import { hashString, initials } from '@/lib/text'

const PALETTE = [
    ['#eef0ff', '#3d3fd1'],
    ['#e8f7ef', '#137a4b'],
    ['#fff4e5', '#b45309'],
    ['#f3ecff', '#6d28d9'],
    ['#e6f4fb', '#0369a1'],
    ['#fdecee', '#be123c'],
    ['#eefaf7', '#0f766e'],
    ['#f1f3f7', '#334155']
]

interface CompanyLogoProps {
    name: string
    logo?: string | null
    logoAlt?: string | null
    size?: 'sm' | 'md' | 'lg' | 'xl'
    className?: string
}

const SIZES = {
    sm: 'size-8 rounded-lg text-xs',
    md: 'size-11 rounded-xl text-sm',
    lg: 'size-14 rounded-2xl text-base',
    xl: 'size-20 rounded-2xl text-2xl'
}

/** The company's uploaded logo, or a monogram tile in a color derived from its name. */
export const CompanyLogo: FC<CompanyLogoProps> = ({ name, logo, logoAlt, size = 'md', className }) => {
    if (logo) {
        return (
            <img
                src={logo}
                alt={logoAlt || `${name} logo`}
                className={cn('shrink-0 border border-border/70 bg-white object-contain p-1', SIZES[size], className)}
                loading='lazy'
            />
        )
    }

    const [background, foreground] = PALETTE[parseInt(hashString(name).slice(0, 4), 16) % PALETTE.length]

    return (
        <span
            aria-hidden='true'
            className={cn('flex shrink-0 items-center justify-center font-heading font-bold select-none', SIZES[size], className)}
            style={{ backgroundColor: background, color: foreground }}
        >
            {initials(name)}
        </span>
    )
}
